import {
    addSurchargeToMinor,
    formatMinorAmount,
    fromMinorUnits,
} from './customAmount';
import { CustomPricingConfig } from './customPricing';
import { reformatDisplayedPrice } from './priceUtils';
import {
    ProductEntity,
    ProductPaymentPlan,
    ProductType,
    VariationType,
} from './types';

export const DEFAULT_VARIATION_ID = 'defvar12';

type Price = { price: number; displayed_price: string };

export interface CustomAmountPricing {
    amountMinor: number | null;
    config: Pick<CustomPricingConfig, 'decimals' | 'format'>;
}

export const findInitialVariation = (variations: VariationType[] = []) =>
    variations.find((variation) => variation.selected_options_values);

/**
 * Get the variation which represents the product without selected choices
 */
const findDefaultVariation = (product: ProductType) =>
    product.variations?.find((variation) => !variation.selected_options_values);

const getItemId = (product: ProductType, variation?: VariationType) => {
    const productId = product.identifier || product.sku;
    const productExternalId = product.external_id || '';
    const {
        identifier: variationId = DEFAULT_VARIATION_ID,
        external_id: variationExternalId = '',
    } = variation || findDefaultVariation(product) || {};

    return `${productId}_${variationId}:${productExternalId}_${variationExternalId}`;
};

const getProductPrice = (
    { price, displayed_price }: ProductType,
    paymentPlan: ProductPaymentPlan | null
): Price =>
    paymentPlan
        ? {
              price: paymentPlan.plan_price,
              displayed_price: paymentPlan.plan_displayed_price,
          }
        : { price, displayed_price };

const getVariationPrice = (
    { price, displayed_price, plans_prices }: VariationType,
    paymentPlan: ProductPaymentPlan | null
): Price => {
    const planPrices = plans_prices.find(
        (prices) => prices.id === paymentPlan?.id
    );
    return planPrices
        ? {
              price: planPrices.price,
              displayed_price: planPrices.displayed_price,
          }
        : { price, displayed_price };
};

const applySurcharge = (
    { price, displayed_price }: Price,
    surcharge: number
): Price => {
    if (!surcharge) {
        return { price, displayed_price };
    }
    const total = price + surcharge;
    return {
        price: total,
        displayed_price: reformatDisplayedPrice(displayed_price, total),
    };
};

/**
 * A pay-what-you-want product is priced at the chosen amount whatever the
 * variation or plan, with the surcharge on top. Its catalog price is never
 * displayed: until there's an amount, the displayed prices are blank.
 */
const applyCustomAmount = (
    { price }: Price,
    surcharge: number,
    { amountMinor, config }: CustomAmountPricing
): Price & { displayed_compare_at: undefined } => {
    if (amountMinor === null) {
        return { price, displayed_price: '', displayed_compare_at: undefined };
    }
    const totalMinor = addSurchargeToMinor(
        amountMinor,
        surcharge,
        config.decimals
    );
    return {
        price: fromMinorUnits(totalMinor, config.decimals),
        displayed_price: formatMinorAmount(
            totalMinor,
            config.decimals,
            config.format
        ),
        displayed_compare_at: undefined,
    };
};

const applyPriceAdjustments = (
    basePrice: Price,
    surcharge: number,
    customAmount?: CustomAmountPricing | null
) =>
    customAmount
        ? applyCustomAmount(basePrice, surcharge, customAmount)
        : applySurcharge(basePrice, surcharge);

// Merges product & variation data & payment plan prices & customizations surcharge
export const buildProductData = ({
    product,
    variation,
    paymentPlan,
    surcharge,
    customAmount,
}: {
    product: ProductType;
    variation?: VariationType;
    paymentPlan: ProductPaymentPlan | null;
    surcharge: number;
    customAmount?: CustomAmountPricing | null;
}): ProductEntity => {
    const itemId = getItemId(product, variation);
    if (variation) {
        return {
            ...product,
            ...variation,
            ...applyPriceAdjustments(
                getVariationPrice(variation, paymentPlan),
                surcharge,
                customAmount
            ),
            image: variation.images[0]?.image || product.image,
            itemId,
            productId: product.identifier,
        };
    }

    return {
        ...product,
        ...applyPriceAdjustments(
            getProductPrice(product, paymentPlan),
            surcharge,
            customAmount
        ),
        itemId,
        productId: product.identifier,
    };
};
